import { useState, useEffect, useRef } from 'react';
import { BookOpen, Plus, Trash2, Edit2, X, Save, Calendar, Paperclip, FileText, Image, ExternalLink, Check, Ban } from 'lucide-react';
import Cookies from 'js-cookie';
import { apiRequest } from '@/utils/api-request';
import { toastMessage } from '@/utils/toast-message';

const BASE = '/api/parent-app/admin';
const apiBase = () => (import.meta.env.VITE_API_URL as string | undefined)?.replace('/api', '') ?? 'http://localhost:5004';

interface PaClass { id: number; class_name: string; academic_year_id: number; curriculum_class_id: number | null; section_id: number | null; }
interface CurriculumClass { id: number; class_label: string; }
interface CurriculumGroup { curriculum_id: number; curriculum_name: string; classes: CurriculumClass[]; }
interface SectionItem { id: number; section_label: string; }
interface PaTeacher { id: number; name: string; }
interface Homework {
  id: number;
  class_id: number;
  subject: string;
  title: string;
  description: string;
  due_date: string;
  attachment_urls: string | null;
  approval_status: 'pending' | 'approved' | 'rejected';
  rejection_reason?: string | null;
  created_by: number | null;
  created_at?: string;
  class?: { id: number; class_name: string };
  teacher?: { id: number; name: string };
}

function parseAttachmentUrls(hw: Homework): string[] {
  if (hw.attachment_urls) {
    try { return JSON.parse(hw.attachment_urls); } catch {}
  }
  return [];
}

const emptyForm = { class_id: '', subject: '', title: '', description: '', due_date: '', teacher_id: '' };

function attachmentIcon(url: string) {
  if (/\.(jpe?g|png|gif|webp)$/i.test(url)) return <Image size={14} />;
  return <FileText size={14} />;
}
function attachmentName(url: string) {
  return decodeURIComponent(url.split('/').pop() ?? 'attachment').replace(/^hw_\d+_[a-z0-9]+\./, 'attachment.');
}

const PAGE_SIZE = 10;

export default function HomeworkPage() {
  const [classes, setClasses] = useState<PaClass[]>([]);
  const [teachers, setTeachers] = useState<PaTeacher[]>([]);
  const [formSubjects, setFormSubjects] = useState<string[]>([]);

  // Form class/section two-step
  const [formCurriculumClassId, setFormCurriculumClassId] = useState<number | ''>('');
  const [formSectionId, setFormSectionId] = useState<number | ''>('');
  const [formSections, setFormSections] = useState<SectionItem[]>([]);

  // Class filter (grouped by curriculum)
  const [classGroups, setClassGroups] = useState<CurriculumGroup[]>([]);
  const [filterCurriculumClassId, setFilterCurriculumClassId] = useState<number | ''>('');
  const [filterSectionId, setFilterSectionId] = useState<number | ''>('');
  const [filterSections, setFilterSections] = useState<SectionItem[]>([]);

  // List filters
  const [filterSubject, setFilterSubject] = useState('');
  const [filterTeacher, setFilterTeacher] = useState<number | ''>('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSubjectOptions, setFilterSubjectOptions] = useState<string[]>([]);
  const [filterTeacherOptions, setFilterTeacherOptions] = useState<PaTeacher[]>([]);

  // Pagination
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);

  const [homework, setHomework] = useState<Homework[]>([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<Homework | null>(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [attachmentFiles, setAttachmentFiles] = useState<File[]>([]);
  const [previews, setPreviews] = useState<(string | null)[]>([]);
  const [removeAttachment, setRemoveAttachment] = useState(false);
  const MAX_FILES = 15;
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Load classes and teachers on mount
  useEffect(() => {
    // Grouped classes for list filter
    apiRequest<any>(`${BASE}/classes`).then(res => {
      setClassGroups(res?.data?.grouped ?? []);
    });
    // Flat pa-classes for the homework form (uses pa_classes.id)
    apiRequest<any>(`${BASE}/pa-classes`).then(res => {
      const list = res?.data?.data ?? res?.data ?? [];
      setClasses(Array.isArray(list) ? list : []);
    });
    apiRequest<any>(`${BASE}/teachers?limit=200`).then(res => {
      const rows = res?.data?.rows ?? res?.data?.data ?? [];
      setTeachers(Array.isArray(rows) ? rows : []);
    });
  }, []);

  // When form curriculum class changes: fetch sections, auto-resolve if no sections
  useEffect(() => {
    setFormSectionId('');
    setFormSections([]);
    setForm(p => ({ ...p, class_id: '', subject: '' }));
    if (!formCurriculumClassId) return;
    apiRequest<any>(`${BASE}/classes/${formCurriculumClassId}/sections`).then(res => {
      const list: SectionItem[] = res?.data?.data ?? [];
      setFormSections(Array.isArray(list) ? list : []);
      if (!list.length) {
        // No sections — resolve directly
        const paClass = classes.find(c => c.curriculum_class_id === Number(formCurriculumClassId));
        if (paClass) setForm(p => ({ ...p, class_id: String(paClass.id) }));
      }
    });
  }, [formCurriculumClassId]);

  // When form section changes: resolve pa_classes.id
  useEffect(() => {
    if (!formCurriculumClassId || !formSectionId) return;
    const paClass = classes.find(
      c => c.curriculum_class_id === Number(formCurriculumClassId) && c.section_id === Number(formSectionId)
    );
    if (paClass) setForm(p => ({ ...p, class_id: String(paClass.id), subject: '' }));
  }, [formSectionId]);

  // Load subjects for the form when class changes
  useEffect(() => {
    if (!form.class_id) { setFormSubjects([]); return; }
    apiRequest<any>(`${BASE}/timetable/subjects?class_id=${form.class_id}`).then(res => {
      const list = res?.data ?? [];
      setFormSubjects(Array.isArray(list) ? list.map((s: any) => (typeof s === 'string' ? s : s.name)) : []);
    });
  }, [form.class_id]);

  // Fetch sections + subject/teacher options when curriculum class filter changes
  useEffect(() => {
    setFilterSectionId('');
    setFilterSubject('');
    setFilterTeacher('');
    if (!filterCurriculumClassId) {  // covers null and ''
      setFilterSections([]);
      setFilterSubjectOptions([]);
      setFilterTeacherOptions([]);
      return;
    }
    apiRequest<any>(`${BASE}/classes/${filterCurriculumClassId}/sections`).then(res => {
      const list = res?.data?.data ?? [];
      setFilterSections(Array.isArray(list) ? list : []);
    });
    // Find any pa_classes.id for this curriculum_class_id to load subject/teacher options
    const paClass = classes.find(c => c.curriculum_class_id === Number(filterCurriculumClassId));
    if (paClass) {
      apiRequest<any>(`${BASE}/timetable/subjects?class_id=${paClass.id}`).then(res => {
        const list = res?.data ?? [];
        setFilterSubjectOptions(Array.isArray(list) ? list.map((s: any) => (typeof s === 'string' ? s : s.name)) : []);
      });
      apiRequest<any>(`${BASE}/pa-classes/teachers?class_id=${paClass.id}`).then(res => {
        const list: PaTeacher[] = res?.data ?? [];
        setFilterTeacherOptions(Array.isArray(list) ? list : []);
      });
    }
  }, [filterCurriculumClassId, classes]);

  const updateSubjectOptions = (_list: Homework[]) => { /* subjects now driven by class filter */ };

  const fetchHomework = (newOffset = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);

    const qs = new URLSearchParams();
    if (filterCurriculumClassId) qs.set('curriculum_class_id', String(filterCurriculumClassId));
    if (filterSectionId) qs.set('section_id', String(filterSectionId));
    if (filterSubject) qs.set('subject', filterSubject);
    if (filterTeacher) qs.set('teacher_id', String(filterTeacher));
    if (filterDateFrom) qs.set('date_from', filterDateFrom);
    if (filterDateTo) qs.set('date_to', filterDateTo);
    if (filterStatus) qs.set('approval_status', filterStatus);
    qs.set('limit', String(PAGE_SIZE));
    qs.set('offset', String(newOffset));

    apiRequest<any>(`${BASE}/homework?${qs}`)
      .then(res => {
        const list: Homework[] = res?.data?.data ?? res?.data ?? [];
        const totalCount: number = res?.data?.total ?? list.length;
        setTotal(totalCount);
        setOffset(newOffset);
        if (append) {
          setHomework(prev => {
            const combined = [...prev, ...list];
            updateSubjectOptions(combined);
            return combined;
          });
        } else {
          setHomework(list);
          updateSubjectOptions(list);
        }
      })
      .finally(() => { setLoading(false); setLoadingMore(false); });
  };

  // Reload when filters change, reset pagination
  useEffect(() => { fetchHomework(0, false); }, [filterCurriculumClassId, filterSectionId, filterSubject, filterTeacher, filterDateFrom, filterDateTo, filterStatus]);

  const openCreate = () => {
    setEditItem(null);
    setForm({ ...emptyForm });
    setFormCurriculumClassId('');
    setFormSectionId('');
    setFormSections([]);
    setAttachmentFiles([]); setPreviews([]);
    setRemoveAttachment(false);
    setShowForm(true);
  };

  const openEdit = (hw: Homework) => {
    setEditItem(hw);
    setForm({
      class_id: String(hw.class_id),
      subject: hw.subject,
      title: hw.title,
      description: hw.description ?? '',
      due_date: hw.due_date ?? '',
      teacher_id: hw.created_by ? String(hw.created_by) : '',
    });
    const paClass = classes.find(c => c.id === hw.class_id);
    const currId = paClass?.curriculum_class_id ?? null;
    setFormCurriculumClassId(currId ?? '');
    setFormSectionId(paClass?.section_id ?? '');
    if (currId) {
      apiRequest<any>(`${BASE}/classes/${currId}/sections`).then(res => {
        const list: SectionItem[] = res?.data?.data ?? [];
        setFormSections(Array.isArray(list) ? list : []);
      });
    } else {
      setFormSections([]);
    }
    setAttachmentFiles([]); setPreviews([]);
    setRemoveAttachment(false);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formCurriculumClassId) { toastMessage('Please select a class', 'error'); return; }
    if (formSections.length > 0 && !formSectionId) { toastMessage('Please select a section', 'error'); return; }
    if (!form.class_id || !form.subject || !form.title || !form.due_date) { toastMessage('Class, subject, title and due date are required', 'error'); return; }
    setSaving(true);
    try {
      const token = Cookies.get('erp_auth_token') || Cookies.get('crm_auth_token');
      const fd = new FormData();
      fd.append('class_id', form.class_id);
      fd.append('subject', form.subject);
      fd.append('title', form.title);
      fd.append('description', form.description);
      if (form.due_date) fd.append('due_date', form.due_date);
      if (form.teacher_id) fd.append('created_by', form.teacher_id);
      attachmentFiles.forEach(f => fd.append('attachments', f));
      if (removeAttachment) fd.append('remove_attachment', 'true');

      const url = editItem
        ? `${apiBase()}${BASE}/homework/${editItem.id}`
        : `${apiBase()}${BASE}/homework`;
      const res = await fetch(url, {
        method: editItem ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token ?? ''}` },
        credentials: 'include',
        body: fd,
      });
      const json = await res.json();
      if (json?.success) {
        toastMessage(editItem ? 'Updated' : 'Created', 'success');
        previews.forEach(p => p && URL.revokeObjectURL(p));
        setShowForm(false);
        fetchHomework(0, false);
      } else {
        toastMessage(json?.message ?? 'Failed to save', 'error');
      }
    } catch { toastMessage('Failed to save', 'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Delete this homework?')) return;
    await apiRequest<any>(`${BASE}/homework/${id}`, { method: 'DELETE' });
    setHomework(prev => prev.filter(h => h.id !== id));
    setTotal(t => t - 1);
    toastMessage('Deleted', 'success');
  };

  const setApproval = async (id: number, status: 'approved' | 'rejected') => {
    let rejection_reason: string | null = null;
    if (status === 'rejected') {
      rejection_reason = prompt('Reason for rejection (optional):') ?? null;
    }
    const res = await apiRequest<any>(`${BASE}/homework/${id}/${status === 'approved' ? 'approve' : 'reject'}`, {
      method: 'PUT',
      body: JSON.stringify({ rejection_reason }),
    }).catch(() => null);
    if (res?.success === false || res === null) { toastMessage('Not allowed or failed', 'error'); return; }
    setHomework(prev => prev.map(h => h.id === id ? { ...h, approval_status: status, rejection_reason } : h));
    toastMessage(status === 'approved' ? 'Approved — now visible to parents' : 'Rejected', 'success');
  };

  const isOverdue = (due: string) => due && new Date(due) < new Date();
  const isDueSoon = (due: string) => {
    if (!due) return false;
    const diff = new Date(due).getTime() - Date.now();
    return diff > 0 && diff < 3 * 24 * 60 * 60 * 1000;
  };

  const hasMore = homework.length < total;
  const activeFilters = [filterCurriculumClassId, filterSubject, filterTeacher, filterDateFrom, filterDateTo, filterSectionId, filterStatus].filter(Boolean).length;

  const STATUS_STYLE: Record<string, { bg: string; fg: string; label: string }> = {
    pending:  { bg: '#f59e0b18', fg: '#f59e0b', label: 'Pending approval' },
    approved: { bg: '#22c55e18', fg: '#22c55e', label: 'Approved' },
    rejected: { bg: '#ef444418', fg: '#ef4444', label: 'Rejected' },
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--bg)' }}>
      {/* Page Header */}
      <div className="page-head">
        <div className="page-head-icon">
          <BookOpen size={24} style={{ color: 'var(--accent)' }} />
        </div>
        <div className="page-head-text">
          <h1>Homework</h1>
          <p>{total} assignment{total !== 1 ? 's' : ''} across all classes</p>
        </div>
        <div className="page-head-actions">
          <button onClick={openCreate} className="btn btn-primary" style={{ gap: '7px' }}>
            <Plus size={17} /> Add Homework
          </button>
        </div>
      </div>


      {/* Drawer Overlay & Form */}
      {showForm && (
        <>
          <div className="scrim" onClick={() => { previews.forEach(p => p && URL.revokeObjectURL(p)); setShowForm(false); }} />
          <div className="drawer">
            <div className="drawer-head">
              <div>
                <h2>{editItem ? 'Edit Homework' : 'Add Homework'}</h2>
                <p>Assign work to a class section</p>
              </div>
              <button onClick={() => { previews.forEach(p => p && URL.revokeObjectURL(p)); setShowForm(false); }} className="icon-btn" style={{ marginLeft: 'auto' }}>
                <X size={20} />
              </button>
            </div>
            <div className="drawer-body" style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Class */}
              <label className="field" style={{ gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                  Class <span style={{ color: 'var(--danger)' }}>*</span>
                </span>
                <select
                  value={formCurriculumClassId}
                  onChange={e => setFormCurriculumClassId(e.target.value ? Number(e.target.value) : '')}
                  className="select"
                >
                  <option value="">Select class</option>
                  {classGroups.map(g => {
                    const filtered = g.classes.filter(gc => classes.some(c => c.curriculum_class_id === gc.id));
                    if (!filtered.length) return null;
                    return (
                      <optgroup key={g.curriculum_id} label={g.curriculum_name}>
                        {filtered.map(gc => <option key={gc.id} value={gc.id}>{gc.class_label}</option>)}
                      </optgroup>
                    );
                  })}
                </select>
              </label>

              {/* Section (shown only when sections exist) */}
              {formSections.length > 0 && (
                <label className="field" style={{ gap: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                    Section <span style={{ color: 'var(--danger)' }}>*</span>
                  </span>
                  <select
                    value={formSectionId}
                    onChange={e => setFormSectionId(e.target.value ? Number(e.target.value) : '')}
                    className="select"
                  >
                    <option value="">Select section</option>
                    {formSections.map(s => <option key={s.id} value={s.id}>{s.section_label}</option>)}
                  </select>
                </label>
              )}

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                {/* Subject */}
                <label className="field" style={{ gap: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                    Subject <span style={{ color: 'var(--danger)' }}>*</span>
                  </span>
                  {formSubjects.length > 0 ? (
                    <select
                      value={form.subject}
                      onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                      className="select"
                    >
                      <option value="">Select subject</option>
                      {formSubjects.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : (
                    <input
                      value={form.subject}
                      onChange={e => setForm(p => ({ ...p, subject: e.target.value }))}
                      placeholder={form.class_id ? 'No subjects — type manually' : 'Select class first'}
                      className="input"
                    />
                  )}
                </label>
                {/* Due Date */}
                <label className="field" style={{ gap: '6px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                    Due Date <span style={{ color: 'var(--danger)' }}>*</span>
                  </span>
                  <input
                    type="date" value={form.due_date}
                    onChange={e => setForm(p => ({ ...p, due_date: e.target.value }))}
                    min={new Date().toISOString().split('T')[0]}
                    className="input"
                  />
                </label>
              </div>

              {/* Teacher */}
              <label className="field" style={{ gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                  Teacher
                </span>
                <select
                  value={form.teacher_id}
                  onChange={e => setForm(p => ({ ...p, teacher_id: e.target.value }))}
                  className="select"
                >
                  <option value="">Select teacher</option>
                  {teachers.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
              </label>

              {/* Title */}
              <label className="field" style={{ gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                  Title <span style={{ color: 'var(--danger)' }}>*</span>
                </span>
                <input
                  value={form.title}
                  onChange={e => setForm(p => ({ ...p, title: e.target.value }))}
                  placeholder="e.g. Complete Exercise 4.2"
                  className="input"
                />
              </label>

              {/* Description */}
              <label className="field" style={{ gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                  Description
                </span>
                <textarea
                  value={form.description}
                  onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
                  placeholder="Additional instructions…"
                  className="textarea"
                  style={{ minHeight: '130px', resize: 'vertical' }}
                />
              </label>

              {/* Attachments */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', letterSpacing: '-0.1px' }}>
                  Attachments <span style={{ fontWeight: '600', color: 'var(--text-3)' }}>(PDF, Word, Image — max 10MB each, up to {MAX_FILES} files)</span>
                </span>
                {/* Existing attachments on edit */}
                {editItem && !removeAttachment && attachmentFiles.length === 0 && (() => {
                  const urls = parseAttachmentUrls(editItem);
                  return urls.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                      {urls.map((url, i) => (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--info-wash)', border: '1px solid var(--info-soft)', borderRadius: 'var(--r-input)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                            <span style={{ color: 'var(--info)', flexShrink: 0 }}>{attachmentIcon(url)}</span>
                            <a href={url} target="_blank" rel="noreferrer"
                              style={{ fontSize: '12px', color: 'var(--info)', textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {attachmentName(url)} <ExternalLink size={12} style={{ flexShrink: 0 }} />
                            </a>
                          </div>
                        </div>
                      ))}
                      <button type="button" onClick={() => setRemoveAttachment(true)}
                        style={{ fontSize: '12px', color: 'var(--danger)', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0 }}>
                        Remove all existing attachments
                      </button>
                    </div>
                  ) : null;
                })()}
                {/* New files selected */}
                {attachmentFiles.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '8px' }}>
                    {attachmentFiles.map((f, i) => (
                      <div key={i} style={{ position: 'relative', width: 80, height: 80, flexShrink: 0, overflow: 'hidden', borderRadius: 'var(--r-input)', border: '1px solid var(--border)', background: 'var(--surface-tint)' }}>
                        {f.type.startsWith('image/') && previews[i] ? (
                          <a href={previews[i]!} target="_blank" rel="noreferrer" style={{ display: 'block', width: '100%', height: '100%' }}>
                            <img src={previews[i]!} alt={f.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </a>
                        ) : (
                          <a href={previews[i]!} target="_blank" rel="noreferrer"
                            style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '4px', padding: '4px', textDecoration: 'none' }}>
                            <FileText size={24} style={{ color: 'var(--text-3)' }} />
                            <span style={{ fontSize: '11px', color: 'var(--text-3)', textAlign: 'center', lineHeight: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '100%' }}>{f.name.split('.').pop()?.toUpperCase()}</span>
                          </a>
                        )}
                        <button type="button"
                          onClick={() => {
                            if (previews[i]) URL.revokeObjectURL(previews[i]!);
                            setAttachmentFiles(prev => prev.filter((_, j) => j !== i));
                            setPreviews(prev => prev.filter((_, j) => j !== i));
                          }}
                          style={{ position: 'absolute', top: 3, right: 3, width: 18, height: 18, background: 'rgba(0,0,0,0.65)', border: 'none', borderRadius: '50%', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>
                          <X size={10} style={{ color: '#fff' }} />
                        </button>
                      </div>
                    ))}
                    {attachmentFiles.length < MAX_FILES && (
                      <button type="button" onClick={() => fileInputRef.current?.click()}
                        style={{ width: 80, height: 80, flexShrink: 0, borderRadius: 'var(--r-input)', border: '2px dashed var(--border-2)', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-3)', cursor: 'pointer', transition: 'border-color 0.2s', fontSize: 0 }}>
                        <Plus size={20} />
                      </button>
                    )}
                  </div>
                )}
                <input ref={fileInputRef} type="file" style={{ display: 'none' }} multiple
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                  onChange={e => {
                    const picked = Array.from(e.target.files ?? []);
                    const newPreviews = picked.map(f => URL.createObjectURL(f));
                    setAttachmentFiles(prev => [...prev, ...picked].slice(0, MAX_FILES));
                    setPreviews(prev => [...prev, ...newPreviews].slice(0, MAX_FILES));
                    setRemoveAttachment(false);
                    e.target.value = '';
                  }} />
                <button type="button" onClick={() => fileInputRef.current?.click()}
                  disabled={attachmentFiles.length >= MAX_FILES}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', borderRadius: 'var(--r-input)', fontSize: '13px', color: 'var(--text-2)', background: 'transparent', border: 'none', cursor: attachmentFiles.length >= MAX_FILES ? 'not-allowed' : 'pointer', opacity: attachmentFiles.length >= MAX_FILES ? 0.5 : 1, transition: 'background 0.2s' }}>
                  <Paperclip size={16} />
                  <span>Attach</span>
                  {attachmentFiles.length > 0 && <span style={{ fontSize: '11px', color: 'var(--accent-deep)', fontWeight: '700' }}>{attachmentFiles.length}/{MAX_FILES}</span>}
                </button>
              </div>
            </div>
            <div className="drawer-foot">
              <button onClick={() => { previews.forEach(p => p && URL.revokeObjectURL(p)); setShowForm(false); }} className="btn btn-ghost">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="btn btn-primary" style={{ gap: '7px' }}>
                <Save size={17} /> {saving ? 'Saving...' : editItem ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Filter bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '12px 32px', borderBottom: '1px solid var(--border)', flexShrink: 0, flexWrap: 'wrap' }}>
        {/* Left: class/section/subject/teacher */}
        <select className="select" style={{ width: 'auto' }} value={filterCurriculumClassId} onChange={e => { setFilterCurriculumClassId(Number(e.target.value) || ''); setFilterSectionId(''); }}>
          <option value="">All Classes</option>
          {classGroups.map(g => (
            <optgroup key={g.curriculum_id} label={g.curriculum_name}>
              {g.classes.map(c => <option key={c.id} value={c.id}>{c.class_label}</option>)}
            </optgroup>
          ))}
        </select>

        {filterCurriculumClassId !== '' && filterSections.length > 0 && (
          <select className="select" style={{ width: 'auto' }} value={filterSectionId} onChange={e => setFilterSectionId(Number(e.target.value) || '')}>
            <option value="">All Sections</option>
            {filterSections.map(s => <option key={s.id} value={s.id}>{s.section_label}</option>)}
          </select>
        )}

        <select className="select" style={{ width: 'auto' }} value={filterSubject} onChange={e => setFilterSubject(e.target.value)} disabled={!filterCurriculumClassId}>
          <option value="">All Subjects</option>
          {filterSubjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
        </select>

        <select className="select" style={{ width: 'auto' }} value={filterTeacher} onChange={e => setFilterTeacher(Number(e.target.value) || '')} disabled={!filterCurriculumClassId}>
          <option value="">All Teachers</option>
          {filterTeacherOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>

        <select className="select" style={{ width: 'auto' }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="">All Statuses</option>
          <option value="pending">Pending approval</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>

        {activeFilters > 0 && (
          <button className="btn btn-ghost" style={{ fontSize: 12, gap: 4 }} onClick={() => { setFilterCurriculumClassId(''); setFilterSectionId(''); setFilterSubject(''); setFilterTeacher(''); setFilterDateFrom(''); setFilterDateTo(''); setFilterStatus(''); }}>
            <X size={12} /> Clear
          </button>
        )}

        {/* Right: date range */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Due From</span>
            <input type="date" className="input" style={{ width: 150 }} value={filterDateFrom} onChange={e => setFilterDateFrom(e.target.value)} />
          </div>
          <span style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 14 }}>→</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Due To</span>
            <input type="date" className="input" style={{ width: 150 }} value={filterDateTo} onChange={e => setFilterDateTo(e.target.value)} />
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px' }}>
        {loading ? (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
            <div style={{ animation: 'spin 1s linear infinite', width: '32px', height: '32px', borderRadius: '50%', borderTop: '3px solid var(--accent)', borderRight: '3px solid transparent' }} />
          </div>
        ) : homework.length === 0 ? (
          <div className="empty">
            <div className="empty-art"><BookOpen size={44} /></div>
            <h3>No assignments yet</h3>
            <p>No homework found for the selected filters</p>
            <button onClick={openCreate} className="btn btn-primary">
              <Plus size={17} /> Add First Assignment
            </button>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, paddingBottom: 8 }}>
              {homework.map(hw => {
                const attachUrls = parseAttachmentUrls(hw);
                const overdue = hw.due_date && isOverdue(hw.due_date);
                const soon    = hw.due_date && !overdue && isDueSoon(hw.due_date);
                const st = STATUS_STYLE[hw.approval_status] ?? STATUS_STYLE.pending;
                const borderColor = hw.approval_status !== 'approved'
                  ? (hw.approval_status === 'rejected' ? '#ef4444' : '#f59e0b')
                  : (overdue ? '#ef4444' : soon ? '#f59e0b' : '#22c55e');
                return (
                  <div key={hw.id} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderLeft: `4px solid ${borderColor}`, borderRadius: 10, padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* Top: subject + status + actions */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 4, background: 'var(--bg)', color: 'var(--text-2)', border: '1px solid var(--border)', fontWeight: 500 }}>{hw.subject}</span>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: st.bg, color: st.fg, fontWeight: 700 }}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: st.fg, display: 'inline-block', marginRight: 4, verticalAlign: 'middle' }} />{st.label}
                      </span>
                      {hw.approval_status === 'approved' && overdue && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#ef444418', color: '#ef4444', fontWeight: 700 }}>Overdue</span>}
                      {hw.approval_status === 'approved' && soon && <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: '#f59e0b18', color: '#f59e0b', fontWeight: 700 }}>Due soon</span>}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                        {hw.approval_status !== 'approved' && (
                          <button onClick={() => setApproval(hw.id, 'approved')} className="icon-btn" style={{ color: '#22c55e' }} title="Approve"><Check size={15} /></button>
                        )}
                        {hw.approval_status !== 'rejected' && (
                          <button onClick={() => setApproval(hw.id, 'rejected')} className="icon-btn" style={{ color: '#f59e0b' }} title="Reject"><Ban size={15} /></button>
                        )}
                        <button onClick={() => openEdit(hw)} className="icon-btn"><Edit2 size={15} /></button>
                        <button onClick={() => handleDelete(hw.id)} className="icon-btn" style={{ color: 'var(--danger)' }}><Trash2 size={15} /></button>
                      </div>
                    </div>
                    {/* Class name */}
                    {hw.class?.class_name && (
                      <div style={{ fontSize: 11, color: 'var(--text-3)', fontWeight: 600 }}>{hw.class.class_name}</div>
                    )}
                    {/* Title */}
                    <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)', lineHeight: 1.3 }}>{hw.title}</div>
                    {/* Description */}
                    {hw.description && (
                      <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{hw.description}</div>
                    )}
                    {/* Rejection reason */}
                    {hw.approval_status === 'rejected' && hw.rejection_reason && (
                      <div style={{ fontSize: 12, color: '#ef4444', background: '#ef444410', border: '1px solid #ef444430', borderRadius: 6, padding: '6px 10px' }}>
                        Rejected: {hw.rejection_reason}
                      </div>
                    )}
                    {/* Footer */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, fontSize: 12, color: 'var(--text-3)', fontWeight: 500 }}>
                      {hw.due_date && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Calendar size={12} /> Due {new Date(hw.due_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        </span>
                      )}
                      {hw.teacher && <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>by {hw.teacher.name}</span>}
                      {attachUrls.length > 0 && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4, marginLeft: 'auto' }}>
                          <Paperclip size={12} /> {attachUrls.length} file{attachUrls.length !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More */}
            {hasMore && (
              <div style={{ textAlign: 'center', paddingTop: 8, paddingBottom: 24 }}>
                <button onClick={() => fetchHomework(offset + PAGE_SIZE, true)} disabled={loadingMore} className="btn btn-soft" style={{ minWidth: 180 }}>
                  {loadingMore ? 'Loading…' : `Load More (${homework.length} of ${total})`}
                </button>
              </div>
            )}
          </>
        )}
      </div>
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
